<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { QExpansionItem } from 'quasar';
import { QSlideTransition } from 'quasar';
import { getLabel } from '@/utils/data-display-utils';

import BaseTag from './base/BaseTag.vue';
import BaseButton from './base/BaseButton.vue';
import BaseComment from './base/BaseComment.vue';
import BaseMediaCard from './base/BaseMediaCard.vue';
import BaseFormAnswer from '@/components/base/BaseFormAnswer.vue';
import RoleVisibilityGuard from './RoleVisibilityGuard.vue';

import { constant } from '@/constant';
import { getTranslation } from '@/utils/data-display-utils';
import { formsService } from '@/services/forms.service';
import { authService } from '@/services/auth.service';
import type { Form } from '@/store/form.Interface';
import { useUserStore } from '@/store/userStore';
import { showToaster } from '@/utils/toaster-util';
import { useTracker } from '@/composables/useTracker';
import { getAltText } from '@/utils/image.helper';
import { Locales } from '@/store/language.interface';

// PROPS
const props = defineProps<{
  consultationId: number;
  consultationSlug: string;
  showQuestionResults: boolean;
  fiche: Form;
  index: number;
  activeSectionIndex: number;
  sectionCount: number;
  formCount: number;
  anonymousComments: boolean;
  displayDemographicData?: boolean;
  languageEnabled?: Locales[];
  sectionProhibitsResultsAndComments?: boolean;
  showAdminResults?: boolean;
}>();

// REFS
const showComments = ref<boolean>(false);
const showContexts = ref<boolean>(false);
const activeFilter = ref<string>('tous');
const hasComments = ref<boolean>(false);
const response = ref<object>();

const userStore = useUserStore();
const { track } = useTracker();

// LIFECYCLE
onMounted(async () => {
  hasComments.value = props.fiche.comments.length > 0;
});

// EMITS
const emit = defineEmits<{
  (e: 'click', index: number): void;
  (e: 'openDemographicDialog', origin: string): void;
  (e: 'openAttachmentDialog', formId: number): void;
}>();

// COMPUTED
const filteredComments = computed(() => {
  if (activeFilter.value === 'tous') {
    return commentData.value;
  } else if (activeFilter.value === 'accord') {
    return commentData.value.filter((comment) => comment.vote.value_id <= 2);
  } else {
    return commentData.value.filter((comment) => comment.vote.value_id > 2);
  }
});

const accordComments = computed(() => {
  return commentData.value.filter((comment) => comment.vote.value_id <= 2);
});

const desaccordComments = computed(() => {
  return commentData.value.filter((comment) => comment.vote.value_id > 2);
});

const commentData = computed(() => {
  return props.fiche.comments;
});

const isFinalQuestion = computed(() => {
  if (props.activeSectionIndex + 1 === props.sectionCount && props.index + 1 === props.formCount) {
    return true;
  } else {
    return false;
  }
});

const isUserLogged = computed(() => {
  return userStore.isUserLogged;
});

const isUserAllowedToComment = computed(() => {
  return props.fiche.response && isUserAllowedToVote.value && props.fiche.response.length > 0;
});

const userHasDemographicsData = computed(() => {
  return userStore.validateDemographicData;
});

const isUserAllowedToVote = computed(
  () => isUserLogged.value && (!props.displayDemographicData || userHasDemographicsData.value),
);

const hasUser = computed(() => !!(props.fiche.user.first_name && props.fiche.user.last_name));

// FUNCTIONS
function toggleContent(type: string) {
  if (type === 'content') {
    showComments.value = false;
    showContexts.value = false;
  } else if (type === 'comments') {
    track({
      event: 'form_comments_view_open',
      params: {
        form_id: props.fiche.id,
        consultation_id: props.consultationId,
      },
    });
    showComments.value = !showComments.value;
    showContexts.value = false;
  }
}

function catchScrollAction(index: number) {
  emit('click', index);
}

async function submitComment(userComment) {
  if (isUserAllowedToComment.value) {
    track({
      event: 'comment_submit',
      params: {
        form_id: props.fiche.id,
        consultation_id: props.consultationId,
        length: userComment.length,
      },
    });
    const comment: any = userComment;
    response.value = await formsService.postComment(comment, props.fiche.id);
    if (response.value.success === true) {
      userComment = '';
      if (response.value.data.flagged) {
        showToaster({
          html: true,
          message: getLabel('dialog.moderation.message.failure', props.languageEnabled, {
            named: { type: getLabel('dialog.moderation.types.comment', props.languageEnabled) },
          }),
          timeout: 0,
          type: 'negative',
        });
      } else {
        showToaster({
          message: getLabel('dialog.moderation.message.success', props.languageEnabled, {
            named: { type: getLabel('dialog.moderation.types.comment', props.languageEnabled) },
          }),
          type: 'positive',
        });
      }
    }
  } else {
    logUserOrAskDemographics('comment');
  }
}

function startComment() {
  track({
    event: 'comment_start',
    params: {
      form_id: props.fiche.id,
      consultation_id: props.consultationId,
    },
  });
}

function filterComments(type: string | null) {
  if (type === 'tous') {
    activeFilter.value = 'tous';
  } else if (type === 'accord') {
    activeFilter.value = 'accord';
  } else if (type === 'desaccord') {
    activeFilter.value = 'desaccord';
  }
}

function submitVote(submittedResponse: any) {
  if (isUserLogged.value) {
    track({
      event: 'vote_click',
      params: {
        form_id: props.fiche.id,
        consultation_id: props.consultationId,
        value: submittedResponse,
      },
      trackWith: { facebook: true },
    });
  }
  if (isUserAllowedToVote.value) {
    formsService.postFormVote(submittedResponse, props.fiche.id).then(() => {
      track({
        event: 'vote_saved',
        params: {
          form_id: props.fiche.id,
          consultation_id: props.consultationId,
          value: submittedResponse,
        },
        trackWith: { facebook: true },
      });
    });
  }
}

function logUserOrAskDemographics(origin) {
  if (!isUserLogged.value) {
    track({
      event: 'unauth_vote_click',
      params: {
        form_id: props.fiche.id,
        consultation_id: props.consultationId,
      },
      trackWith: { facebook: true },
    });
    track({
      event: 'login_click',
      params: {
        location: 'vote_redirect',
      },
    });
    authService.login({
      consultation: {
        consultationId: props.consultationSlug,
        section: props.fiche.sectionId,
        form: props.index,
      },
    });
  } else if (props.displayDemographicData && !userHasDemographicsData.value) {
    emit('openDemographicDialog', origin);
  }
}

async function submitMarker(marker: { marker_location: object[]; marker_text: string }) {
  const response = await formsService.postMapVote(marker, props.fiche);
  if (response.data.moderationStatus === constant.FormComments.ModerationStatus.Pending) {
    showToaster({
      html: true,
      message: getLabel('dialog.moderation.message.failure', props.languageEnabled, {
        named: { type: getLabel('dialog.moderation.types.vote', props.languageEnabled) },
      }),
      timeout: 0,
      type: 'negative',
    });
  } else {
    showToaster({
      message: getLabel('dialog.moderation.message.success', props.languageEnabled, {
        named: { type: getLabel('dialog.moderation.types.vote', props.languageEnabled) },
      }),
      type: 'positive',
    });
    track({
      event: 'map_pin_complete',
      params: {
        form_id: props.fiche.id,
        consultation_id: props.consultationId,
        coordinates: marker.marker_location.join(', '),
        description_length: marker.marker_text.length,
      },
    });
  }
}

function sliderSelected(sliderValue) {
  track({
    event: 'slider_select',
    params: {
      form_id: props.fiche.id,
      consultation_id: props.consultationId,
      value: sliderValue.valueId,
    },
  });
}

function mapStart() {
  track({
    event: 'map_pin_start',
    params: {
      form_id: props.fiche.id,
      consultation_id: props.consultationId,
    },
  });
}

function openAttachmentDialog() {
  emit('openAttachmentDialog', props.fiche.id);
}
</script>

<template>
  <div class="base-form shadow" :id="'fiche_' + props.index">
    <div class="base-form__content">
      <q-badge
        v-if="props.fiche.is_citizen_proposal && !anonymousComments && props.displayDemographicData && hasUser"
        class="q-pa-md q-mb-md base-form__content-proposal"
      >
        {{ getLabel('form.citizen-proposal.label', props.languageEnabled) }}
        {{ $props.fiche.user.first_name }} {{ $props.fiche.user.last_name }}
      </q-badge>
      <q-badge
        v-else-if="props.fiche.is_citizen_proposal && (anonymousComments || !props.displayDemographicData || !hasUser)"
        class="q-pa-md q-mb-md"
      >
        {{ getLabel('form.citizen-proposal.anonymous-label', props.languageEnabled) }}
      </q-badge>
      <div class="base-form__header" @click="toggleContent('content')">
        <div class="base-form__header-title">
          <h3>
            {{ getTranslation(props.fiche.question, 'title', props.languageEnabled) }}
          </h3>
        </div>
      </div>
      <div class="base-form__description-section" @click="toggleContent('content')">
        <span class="base-form__description">
          {{ getTranslation(props.fiche.question, 'content', props.languageEnabled) }}
        </span>
      </div>
      <div class="base-form__context" v-if="fiche.context?.length > 0">
        <q-expansion-item v-bind:default-opened="props.fiche.attachmentsOpened">
          <template v-slot:header>
            <q-item-section>
              <span class="title">
                {{ getLabel('form.additional-information', props.languageEnabled) }}
                <span class="count">[ {{ fiche.context.length }} ]</span>
              </span>
            </q-item-section>
          </template>
          <q-card v-for="context in fiche.context">
            <BaseMediaCard
              :type="context.type"
              :title="getTranslation(context, 'title', props.languageEnabled)"
              :content="getTranslation(context, 'content', props.languageEnabled)"
              @open-attachment-dialog="openAttachmentDialog()"
            />
          </q-card>
        </q-expansion-item>
      </div>
      <role-visibility-guard :roles-allowed="[constant.User.Role.ADMIN]">
        <div class="base-form__total-votes" v-if="fiche.statistiques">
          <q-icon name="people" />
          {{ fiche.statistiques.totalCount }}
          {{ getLabel('form.total-votes', props.languageEnabled) }}
        </div>
      </role-visibility-guard>
      <div class="base-form__image" v-if="fiche.question.image?.url">
        <img
          class="base-form__image"
          :src="fiche.question.image?.url"
          :alt="getAltText(fiche.question.image, 'alt-text.question.image', props.languageEnabled)"
        />
      </div>
      <BaseFormAnswer
        :show-question-results="props.showQuestionResults"
        :answers="fiche.answers"
        :question-response="fiche.response && fiche.response[0]?.votes"
        :statistique="fiche.statistiques"
        :index="index"
        :form-type="fiche.type"
        :response="fiche.response || []"
        :max-answers="fiche.maxAnswers"
        :is-user-allowed-to-vote="isUserAllowedToVote"
        :is-final-question="isFinalQuestion"
        :i-believe-i-care-enabled="fiche.iBelieveICareEnabled"
        :are-comments-enabled="fiche.areCommentsEnabled"
        :map-markers="fiche.mapMarkers"
        :language-enabled="props.languageEnabled"
        :section-prohibits-results-and-comments="props.sectionProhibitsResultsAndComments"
        :show-admin-results="props.showAdminResults"
        @next-form="catchScrollAction($event)"
        @submit="submitVote"
        @vote-not-allowed="logUserOrAskDemographics('vote')"
        @submit-comment="submitComment($event)"
        @start-comment="startComment"
        @submit-marker="submitMarker"
        @slider-selected="sliderSelected"
        @map-start="mapStart"
      />
      <q-separator />

      <div class="base-form__footer" v-if="!props.sectionProhibitsResultsAndComments">
        <div class="base-form__footer-right" v-if="props.fiche.areCommentsEnabled">
          <BaseTag
            class="text-primary tag-button"
            icon="o_mode_comment"
            icon-color="primary"
            iconRight
            @click="toggleContent('comments')"
          >
            <template v-slot:title> {{ getLabel('consultation.comment', props.languageEnabled) }}</template>
            <template v-slot:number>{{ commentData ? commentData.length : 0 }}</template>
          </BaseTag>
        </div>
      </div>

      <q-slide-transition v-if="props.fiche.type != constant.Form.Type.Map" :duration="600">
        <div class="base-form__comments-container" v-if="showComments">
          <q-separator />
          <div class="base-form__comments-filterbuttons" v-if="fiche.type === constant.Form.Type.Proposal">
            <BaseButton
              :class="{ active: activeFilter === 'tous' }"
              class="blue-secondary"
              @click="filterComments('tous')"
            >
              {{ getLabel('buttons.all', props.languageEnabled) }}
              <span class="count"> [ {{ commentData.length }} ] </span>
            </BaseButton>
            <BaseButton
              :class="{ active: activeFilter === 'accord' }"
              class="blue-secondary"
              @click="filterComments('accord')"
            >
              {{ getLabel('buttons.agree', props.languageEnabled) }}
              <span class="count"> [ {{ accordComments.length }} ] </span>
            </BaseButton>
            <BaseButton
              :class="{ active: activeFilter === 'desaccord' }"
              class="blue-secondary"
              @click="filterComments('desaccord')"
            >
              {{ getLabel('buttons.disagree', props.languageEnabled) }}
              <span class="count"> [ {{ desaccordComments.length }} ] </span>
            </BaseButton>
          </div>
          <div class="row base-form__comments-anonymous_notice" v-if="props.anonymousComments">
            <div class="col-auto base-form__comments-anonymous_notice-icon">
              <q-icon size="16px" name="info" color="primary" />
            </div>
            <div class="col">{{ getLabel('consultation.anonymous-consultation.notice', props.languageEnabled) }}</div>
          </div>
          <div class="base-form__comments-section" v-if="showComments">
            <div v-for="comment in filteredComments">
              <BaseComment
                :userComment="comment"
                :is-proposal="fiche.type === constant.Form.Type.Proposal"
                :formId="fiche.id"
                :show-question-results="props.showQuestionResults"
                :display-demographic-data="props.displayDemographicData"
                :language-enabled="props.languageEnabled"
              />
            </div>
            <div v-if="filteredComments.length < 1" class="base-form__comments-section-none">
              {{ getLabel('consultation.no-comments', props.languageEnabled) }}
            </div>
          </div>
        </div>
      </q-slide-transition>
      <q-slide-transition :duration="600">
        <div class="base-form__contexts-section" v-if="showContexts">
          <div v-for="context in fiche.context">
            <BaseMediaCard
              :type="context.type"
              :title="getTranslation(context, 'title', props.languageEnabled)"
              :content="getTranslation(context, 'content', props.languageEnabled)"
            />
          </div>
        </div>
      </q-slide-transition>
    </div>
  </div>
</template>

<style lang="scss">
.base-form {
  height: 100%;
  width: 100%;
  align-self: start;
  display: flex;
  flex-direction: column;
  background-color: $color-neutral-white;
  border-radius: 8px;
  transition: all 200ms ease;

  &__image {
    position: relative;
    background-color: $color-neutral-grey-300;
    border-radius: 8px;
    display: flex;
    justify-content: center;
    margin: 0 10%;
    padding: 6px;
    max-width: 100%;
  }

  &__fiche-type {
    position: absolute;
    top: $space-md;
    right: $space-lg;
    background-color: $color-neutral-grey-200;
    color: $color-neutral-black;
    padding: 0 $space-xs;
    border-radius: 8px;
  }

  &__tags {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    .left-tags,
    .right-tags {
      display: flex;
      flex-direction: row;
    }

    .base-tag {
      margin-left: 2px;
      margin-right: 2px;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: $space-md;
    width: 100%;
    height: 100%;

    .moderator-comments {
      display: flex;
      flex-direction: column;
      margin: $space-md $space-sm;
      border: solid 4px $color-red-12;

      &__title {
        display: flex;
        justify-content: center;
        background-color: $color-red-12;
        padding: $space-sm;
      }

      &__content {
        display: flex;
        padding: $space-sm;
      }
    }

    &-proposal {
      white-space: normal !important;
    }
  }

  .active {
    background-color: $color-primary-50;
  }

  &__header {
    display: flex;
    flex-direction: row;
    color: $color-primary-darken;
    gap: 6px;

    &-icon {
      .q-icon {
        font-size: $h1;
      }
    }

    &-title {
      display: flex;
      align-items: center;
    }
  }

  &__description-section {
    padding: $space-sm 0;
    color: $color-neutral-grey-400;
  }

  &__context {
    padding: $space-sm 0;
    .title {
      display: flex;
      flex-direction: row;
      gap: $space-sm;

      .count {
        display: flex;
        align-items: center;
        white-space: nowrap;
      }
    }
  }

  &__title {
    font-size: 16px;
    display: flex;
    font-weight: bold;
  }

  &__form {
    display: flex;
    flex-direction: column;

    .q-btn {
      margin-top: 5px;
      margin-bottom: 5px;
    }
  }

  &__actions {
    display: flex;
    flex-direction: row;
    margin-top: 10px;
    align-items: flex-end;
    gap: 4px;

    &-poll {
      display: flex;
      flex-direction: row;
      width: 100%;
    }

    &-cancel {
      margin-right: 5px;
      width: 100%;
    }

    &-submit {
      margin-left: 5px;
      width: 100%;
    }

    &-stats {
      width: 100%;
    }

    &-next {
      .q-btn .q-icon {
        font-size: $large-title !important;
      }
    }

    .q-btn {
      margin-top: 5px;
      margin-bottom: 5px;
    }
  }

  .q-separator {
    margin: $space-sm 0;
  }

  &__footer {
    display: flex;
    flex-direction: row;
    justify-content: end;
    align-items: flex-end;
    margin-top: $space-sm;
    cursor: pointer;

    .tag-button {
      height: 35px;
      padding: 10px;
      border-radius: 8px;
      font-size: $body-text;
    }

    &-left .base-tag {
      min-height: 10px;

      .q-icon {
        padding: 0px 3px;
      }
    }

    &-right {
      display: flex;
      flex-direction: row;

      .q-icon {
        margin: 0px 3px;
        padding: 0px !important;
      }
    }
  }

  &__comments {
    &-filterbuttons {
      display: flex;
      justify-content: space-between;
      background-color: $color-neutral-white;
      position: sticky;
      top: 0px;
      gap: 10px;

      button {
        margin: 2px;
        padding: 0.3rem;
        width: 100%;

        .count {
          padding-left: 5px;
          white-space: nowrap;
        }
      }
    }

    &-section {
      overflow-y: scroll;
      overflow-x: hidden;
      max-height: 300px;
      padding: 0 $space-sm;
      margin: $space-sm 0;
      background-color: $color-neutral-grey-100;
      border-radius: 8px;

      &-none {
        padding: $space-sm;
      }

      //scrollbar styles
      &::-webkit-scrollbar {
        border-radius: 5px;
        width: 7px;
      }

      &::-webkit-scrollbar-track {
        background-color: transparent;
      }

      &::-webkit-scrollbar-thumb {
        background-color: $color-neutral-grey-400-50;
        border-radius: 10px;
      }
    }

    &-anonymous_notice {
      padding: $space-sm;
      background-color: $color-primary-12;
      border-radius: 8px;
      margin: $space-sm 0;

      font-size: $small-text;

      &-icon {
        margin-right: $space-sm;
      }
    }
  }

  &__total-votes {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin: 0 $space-sm $space-md $space-sm;
    gap: $space-sm;

    .q-icon {
      font-size: $small-title;
      color: $color-primary-darken;
    }
  }
}

.base-tag {
  &.count {
    padding-right: 0px;
  }

  &.highlight {
    color: $color-primary-bright;
  }
}

.flexgrow {
  flex-grow: 2;
}

@media screen and (min-width: $breakpoint-md) {
  .base-form__content {
    padding: $space-lg;
  }
}
@media screen and (max-width: $breakpoint-sm) {
  .base-form__comments-filterbuttons {
    gap: 0;
  }
}
</style>
